import { useState } from "react";

import {
  Box,
  Card,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Grid,
} from "@mui/material";

import DevicesRoundedIcon from '@mui/icons-material/Watch';
import AccessibilityNewRoundedIcon from "@mui/icons-material/AccessibilityNewRounded";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import WifiOffRoundedIcon from "@mui/icons-material/WifiOffRounded";
import WifiRoundedIcon from "@mui/icons-material/WifiRounded";

import {
  getDevicesByTimeRange,
  getAllMsgDetails,
} from "../../services/dashboard.api";

/* ───────────────────────────────────────────── */
/* CARD */
/* ───────────────────────────────────────────── */

function StatCard({ title, value, sub, rightText, icon, iconColor,  textColor,onClick }) {
  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        flex: 1,
        background: "#ffffff",
        border: "1px solid #dbe2ea",
        borderRadius: "12px",
        position: "relative",
        overflow: "visible",
        transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
        cursor: onClick ? "pointer" : "default",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",

        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: "-120%",
          width: "80%",
          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)",
          transform: "skewX(-20deg)",
          transition: "0.7s",
        },

        "&:hover": {
          transform: "translateY(-5px)",
          borderColor: iconColor,
          boxShadow: `0 10px 25px rgba(15,23,42,0.08), 0 0 0 1px ${iconColor}20, 0 0 18px ${iconColor}25`,
        },

        "&:hover::before": { left: "140%" },
      }}
    >
      <Box
  sx={{
    px: 2.5,
    py: 2,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 2,
  }}
>
  {/* LEFT SIDE */}
  <Box sx={{ flex: 1 }}>
    
    {/* TITLE + ICON */}
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Typography
        sx={{
          color: "#64748b",
          fontSize: "0.72rem",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          fontWeight: 700,
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>

      <Box
        sx={{
          color: iconColor,
          display: "flex",
          alignItems: "center",
        }}
      >
        {icon}
      </Box>
    </Box>

    {/* RIGHT TEXT */}
    <Typography
      sx={{
        color: textColor || iconColor,
        fontSize: "0.85rem",
        fontWeight: 700,
        mt: 0.8,
      }}
    >
      {rightText}
    </Typography>
  </Box>

  {/* VALUE */}
  <Box sx={{ textAlign: "center", minWidth: 60 }}>
    <span
      style={{
        color: "#111827",
        fontSize: "32px",
        fontWeight: 600,
        lineHeight: 1,
        letterSpacing: "-0.04em",
        display: "block",
      }}
    >
      {value ?? "NA"}
    </span>
  </Box>
</Box>
    </Card>
  );
}

/* ───────────────────────────────────────────── */
/* DEVICES POPUP */
/* ───────────────────────────────────────────── */

function DevicesDialog({ open, onClose, onlineDevices = [], offlineDevices = [] }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { pt: 2, borderRadius: "16px", overflow: "hidden" } }}>
      <DialogTitle sx={{ fontWeight: 700, color: "#1f2937", px: 3, pt: 2, pb: 2, borderBottom: "1px solid #e5e7eb" }}>
        Device Status
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 3, pt: 3 }}>
        {/* ONLINE */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <WifiRoundedIcon sx={{ color: "#16a34a" }} />
            <Typography sx={{ fontWeight: 700, color: "#16a34a", fontSize: "1.1rem" }}>
              Online ({onlineDevices.length})
            </Typography>
          </Box>
          {onlineDevices.length === 0 ? (
            <Typography sx={{ color: "#374151", fontSize: "1rem" }}>No online devices</Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {onlineDevices.map((id) => (
                <Chip key={id} label={id} sx={{ backgroundColor: "#dcfce7", color: "#15803d", fontWeight: 700, px: 1, py: 2.5, borderRadius: "999px" }} />
              ))}
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* OFFLINE */}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <WifiOffRoundedIcon sx={{ color: "#dc2626" }} />
            <Typography sx={{ fontWeight: 700, color: "#dc2626", fontSize: "1.1rem" }}>
              Offline ({offlineDevices.length})
            </Typography>
          </Box>
          {offlineDevices.length === 0 ? (
            <Typography sx={{ color: "#374151", fontSize: "1rem" }}>No offline devices</Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {offlineDevices.map((id) => (
                <Chip key={id} label={id} sx={{ backgroundColor: "#fee2e2", color: "#dc2626", fontWeight: 700, px: 1, py: 2.5, borderRadius: "999px" }} />
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────────────────────────────────────── */
/* FALL ALARM POPUP  ← UPDATED */
/* ───────────────────────────────────────────── */

function FallAlarmDialog({ open, onClose, alarmDetails = [] }) {
  const [detailItem, setDetailItem] = useState(null);


  return (
    <Dialog
      open={open}
      onClose={() => { setDetailItem(null); onClose(); }}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { pt: 2, borderRadius: "16px", overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{ fontWeight: 700, color: "#1f2937", px: 3, pt: 2, pb: 2, borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 1 }}
      >
       
        {detailItem && (
          <Button
            size="small"
            onClick={() => setDetailItem(null)}
            sx={{ mr: 1, textTransform: "none", fontWeight: 600, color: "#6b7280" }}
          >
            ← Back
          </Button>
        )}
        {detailItem ? "Alarm Details" : "Fall Alarm Details"}
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 3, pt: 3 }}>

       
        {detailItem ? (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>Alarm Type</Typography>
              <Typography sx={{ color: "#ef4444", fontWeight: 700 }}>{detailItem.msgType || "NA"}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>Device ID</Typography>
              <Typography sx={{ fontFamily: "monospace" }}>{detailItem.deviceID || "NA"}</Typography>
            </Grid>

            <Grid item xs={12}>
  <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>
    Name
  </Typography>
  <Typography>
    *******
  </Typography>
</Grid>

<Grid item xs={12}>
  <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>
    Location
  </Typography>
  <Typography>
    *******
  </Typography>
</Grid>

            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>Timestamp</Typography>
              <Typography>{detailItem.time || "NA"}</Typography>
            </Grid>

            {/* <Grid item xs={12}>
              <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>Payload</Typography>
              <Typography sx={{ wordBreak: "break-all", fontFamily: "'IBM Plex Mono', monospace" }}>
                {detailItem.data || "NA"}
              </Typography>
            </Grid> */}
          </Grid>

        ) : (
          
          alarmDetails.length === 0 ? (
            <Typography>No alarms found</Typography>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "14px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                    <TableCell sx={{ fontWeight: 700, color: "#374151" }}>Timestamp</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#374151" }}>Device ID</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#374151" }}>Alarm Type</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#374151" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {alarmDetails.map((row, index) => (
                    <TableRow key={index} sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}>
                      <TableCell>{row.time || "NA"}</TableCell>
                      <TableCell>
                        <Typography sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}>{row.deviceID || "NA"}</Typography>
                      </TableCell>
                      <TableCell>{row.msgType || "NA"}</TableCell>
                      <TableCell>
                        
                        <Button size="small" onClick={() => setDetailItem(row)}>View</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ───────────────────────────────────────────── */
/* HEARTBEAT POPUP  ← UPDATED */
/* ───────────────────────────────────────────── */

function HeartbeatDialog({ open, onClose, heartbeatDevices = [] }) {
  const [detailItem, setDetailItem] = useState(null);

  return (
    <Dialog
      open={open}
      onClose={() => { setDetailItem(null); onClose(); }}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { pt: 2, borderRadius: "16px", overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{ fontWeight: 700, color: "#1f2937", px: 3, pt: 2, pb: 2, borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 1 }}
      >
       
        {detailItem && (
          <Button
            size="small"
            onClick={() => setDetailItem(null)}
            sx={{ mr: 1, textTransform: "none", fontWeight: 600, color: "#6b7280" }}
          >
            ← Back
          </Button>
        )}
        {detailItem ? "Heartbeat Details" : "Heartbeat Devices"}
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 3, pt: 3 }}>

      
        {detailItem ? (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>Device ID</Typography>
              <Typography sx={{ fontFamily: "monospace" }}>{detailItem.deviceID || "NA"}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>Message Type</Typography>
              <Typography>{detailItem.msgType || "NA"}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>Timestamp</Typography>
              <Typography>{detailItem.time || "NA"}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>PAN ID</Typography>
              <Typography>{detailItem.panId ?? "NA"}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 700, color: "#6b7280" }}>HAN Channel No.</Typography>
              <Typography>{detailItem.hanChannelNo ?? "NA"}</Typography>
            </Grid>
          </Grid>

        ) : (
          
          heartbeatDevices.length === 0 ? (
            <Typography>No heartbeat devices</Typography>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "14px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                    <TableCell sx={{ fontWeight: 700, color: "#374151" }}>Timestamp</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#374151" }}>Device ID</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#374151" }}>PAN ID</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#374151" }}>HAN Channel</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#374151" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {heartbeatDevices.map((row, index) => (
                    <TableRow key={index} sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}>
                      <TableCell>{row.time || "NA"}</TableCell>
                      <TableCell>
                        <Typography sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}>{row.deviceID || "NA"}</Typography>
                      </TableCell>
                      <TableCell>{row.panId ?? "NA"}</TableCell>
                      <TableCell>{row.hanChannelNo ?? "NA"}</TableCell>
                      <TableCell>
                       
                        <Button size="small" onClick={() => setDetailItem(row)}>View</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ───────────────────────────────────────────── */
/* MAIN */
/* ───────────────────────────────────────────── */

function StatCards({ totalDevices, offlineCount, fallAlarms, lastAlarmTime, heartbeats }) {
  const [onlineDevices, setOnlineDevices] = useState([]);
  const [offlineDevices, setOfflineDevices] = useState([]);
  const [fallAlarmDetails, setFallAlarmDetails] = useState([]);
  const [heartbeatDevices, setHeartbeatDevices] = useState([]);
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [fallAlarmOpen, setFallAlarmOpen] = useState(false);
  const [heartbeatOpen, setHeartbeatOpen] = useState(false);

  return (
    <>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2, mt: 2 , alignSelf: "flex-start",
    height: "auto",width: "100%" }}>
        <StatCard
          title="Total Devices"
          value={totalDevices}
          rightText={`${totalDevices - offlineCount} Online`}
          sub="Connectivity Health"
          icon={<DevicesRoundedIcon sx={{ fontSize: 20 }} />}
          iconColor={
            totalDevices - offlineCount > 0
              ? "#16a34a"
              : "#dc2626"
          }
          textColor={
            totalDevices - offlineCount > 0
              ? "#16a34a"
              : "#dc2626"
          }
          onClick={async () => {
            try {
              const response = await getDevicesByTimeRange("5m");
              setOnlineDevices(response?.activeDevices || []);
              setOfflineDevices(response?.inactiveDevices || []);
              setDevicesOpen(true);
            } catch (error) { console.log(error); }
          }}
        />

        <StatCard
          title="Fall Alarms (Last 24h)"
          value={fallAlarms}
          rightText={`Last: ${lastAlarmTime || "NA"}`}
          sub="Critical Incidents"
          icon={<AccessibilityNewRoundedIcon sx={{ fontSize: 20 }} />}
          iconColor="#dc2626"
          onClick={async () => {
            try {
              const response = await getAllMsgDetails("Fall detector alarm", "24h");
              setFallAlarmDetails(response || []);
              setFallAlarmOpen(true);
            } catch (error) { console.log(error); }
          }}
        />

        <StatCard
          title="Heartbeats (Last 1H)"
          value={heartbeats}
          rightText="Stable"
          sub="System Pulse"
          icon={<FavoriteBorderRoundedIcon sx={{ fontSize: 20 }} />}
          iconColor="#2563eb"
          onClick={async () => {
            try {
              const response = await getAllMsgDetails("Gateway status", "1h");
              setHeartbeatDevices(response || []);
              setHeartbeatOpen(true);
            } catch (error) { console.log(error); }
          }}
        />
      </Box>

      <DevicesDialog open={devicesOpen} onClose={() => setDevicesOpen(false)} onlineDevices={onlineDevices} offlineDevices={offlineDevices} />
      <FallAlarmDialog open={fallAlarmOpen} onClose={() => setFallAlarmOpen(false)} alarmDetails={fallAlarmDetails} />
      <HeartbeatDialog open={heartbeatOpen} onClose={() => setHeartbeatOpen(false)} heartbeatDevices={heartbeatDevices} />
    </>
  );
}

export default StatCards;